import React, { useState, useRef, useEffect } from 'react'
import { SPINE_COLORS } from '../../utils/colours.js'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'

function parseCSV(text) {
  const rows = []
  const re = /("(?:[^"]|"")*"|[^,\r\n]*)(,|\r?\n|$)/g
  let row = []
  let match
  while ((match = re.exec(text)) !== null) {
    let val = match[1]
    if (val.startsWith('"')) val = val.slice(1, -1).replace(/""/g, '"')
    row.push(val)
    if (match[2] !== ',') {
      if (row.length > 1 || row[0] !== '') rows.push(row)
      row = []
    }
    if (match[2] === '' && match[1] === '') break
  }
  return rows
}

// Column name aliases — maps spreadsheet headers to internal fields
const COL_ALIASES = {
  title:           ['title', 'book', 'book title', 'name'],
  author:          ['author', 'author name', 'by', 'writer'],
  genre:           ['genre', 'category', 'shelf', 'bookshelf', 'subject'],
  series:          ['series', 'series name', 'book series', 'saga'],
  continuesSeries: ['continue series', 'continuing series', 'continue', 'will continue'],
  type:            ['type', 'format', 'book type', 'medium'],
  status:          ['status', 'read status', 'reading status'],
  pages:           ['pages', 'page count', 'num pages', 'number of pages'],
  time:            ['hours', 'duration', 'listening hours', 'audio hours'],
  date:            ['date', 'date finished', 'date completed', 'finished', 'date read'],
  rating:          ['rating', 'stars', 'score', 'my rating'],
  review:          ['review', 'notes', 'comment', 'thoughts', 'my review'],
}

const TYPE_MAP   = { physical: ['physical','print','paper','paperback','hardback','hardcover'], audiobook: ['audiobook','audio','listen','spoken'], ebook: ['ebook','e-book','kindle','digital','epub'] }
const STATUS_MAP = { completed: ['completed','read','done','finished'], reading: ['reading','currently reading','in progress'], abandoned: ['abandoned','dnf','dropped'], wishlist: ['wishlist','want','want to read','tbr'] }

function matchCol(headers, aliases) {
  const lower = headers.map(h => h.toLowerCase().trim())
  for (const a of aliases) {
    const i = lower.indexOf(a.toLowerCase())
    if (i !== -1) return headers[i]
  }
  return null
}

function buildMapping(headers) {
  const map = {}
  for (const [field, aliases] of Object.entries(COL_ALIASES)) {
    const col = matchCol(headers, aliases)
    if (col) map[field] = col
  }
  return map
}

function normType(raw)   { const v = String(raw||'').toLowerCase(); for (const [k,vals] of Object.entries(TYPE_MAP))   { if (vals.some(x => v.includes(x))) return k } return 'physical' }
function normStatus(raw) { const v = String(raw||'').toLowerCase(); for (const [k,vals] of Object.entries(STATUS_MAP)) { if (vals.some(x => v.includes(x))) return k } return 'completed' }
function normDate(raw)   {
  if (!raw) return ''
  // Excel serial number (e.g. 45290)
  if (typeof raw === 'number') return new Date(Math.round((raw - 25569) * 86400000)).toISOString().slice(0,10)
  const s = String(raw).trim()
  // dd/mm/yyyy
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`
  // yyyy-mm-dd (ISO)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  // Fallback — let the browser try (handles "March 15, 2024" etc.)
  const parsed = new Date(s)
  return isNaN(parsed) ? '' : parsed.toISOString().slice(0,10)
}
function normRating(raw) { const n = parseFloat(raw); return isNaN(n) ? 0 : Math.min(5, Math.max(0, Math.round(n * 2) / 2)) }
function normContinues(raw) {
  if (!raw) return null
  const v = String(raw).toLowerCase().trim()
  if (['yes', 'y', 'true', '1'].includes(v)) return true
  if (['no', 'n', 'false', '0'].includes(v)) return false
  return null
}

export default function BookImport() {
  const [books, setBooks] = useState([])
  const [rows, setRows]   = useState([])

  useEffect(() => { api.getBooks().then(d => setBooks(d || [])) }, [])
  const [mapping, setMapping] = useState({})
  const [headers, setHeaders] = useState([])
  const [done, setDone]   = useState('')
  const [dragging, setDragging] = useState(false)
  const fileRef = useRef()

  function processFile(file) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const raw = parseCSV(e.target.result)
      if (raw.length < 2) return
      const hdrs     = raw[0].map(h => String(h).trim()).filter(Boolean)
      const dataRows = raw.slice(1).filter(r => r.some(c => c !== ''))
      const parsed   = dataRows.map(r => { const o = {}; hdrs.forEach((h, i) => { o[h] = r[i] ?? '' }); return o })
      setHeaders(hdrs)
      setRows(parsed)
      setMapping(buildMapping(hdrs))
    }
    reader.readAsText(file)
  }

  function confirmImport() {
    const existing = new Set(books.map(b => b.title.toLowerCase().trim()))
    let added = 0
    const newBooks = [...books]
    rows.forEach(row => {
      const title = String(row[mapping.title] || '').trim()
      if (!title || existing.has(title.toLowerCase())) return
      const type = normType(row[mapping.type])
      const series = String(row[mapping.series] || '').trim()
      newBooks.push({
        id:              Date.now() + added,
        title,
        author:          String(row[mapping.author] || '').trim(),
        genre:           String(row[mapping.genre]  || '').trim(),
        series,
        continuesSeries: series ? normContinues(row[mapping.continuesSeries]) : null,
        type,
        status:          normStatus(row[mapping.status]),
        pages:           type !== 'audiobook' ? (parseInt(row[mapping.pages]) || 0) : 0,
        time:            type === 'audiobook'  ? (parseFloat(row[mapping.time]) || 0) : 0,
        date:            normDate(row[mapping.date]),
        rating:          normRating(row[mapping.rating]),
        review:          String(row[mapping.review] || '').trim(),
        color:           SPINE_COLORS[(newBooks.length + added) % SPINE_COLORS.length],
      })
      existing.add(title.toLowerCase())
      added++
    })
    setBooks(newBooks)
    api.importBooks(newBooks.slice(-added))
    setDone(`${added} book${added !== 1 ? 's' : ''} added ✓`)
    setTimeout(() => { setRows([]); setMapping({}); setHeaders([]); setDone('') }, 3000)
  }

  function downloadTemplate() {
    const rows = [
      ['Title','Author','Genre','Series','Continue Series','Type','Status','Pages','Hours','Date','Rating','Review'],
      ['The Midnight Library','Matt Haig','Fiction','','','Physical','Completed',304,'','15/03/2024',5,'A beautiful, moving book.'],
      ['Project Hail Mary','Andy Weir','Sci-Fi','','','Audiobook','Completed','',16,'10/02/2024',5,'Gripping from start to finish.'],
      ['The Way of Kings','Brandon Sanderson','Fantasy','The Stormlight Archive','Yes','Physical','Completed',1007,'','22/01/2024',4.5,'Epic fantasy at its finest.'],
    ]
    const csvText = rows.map(r =>
      r.map(v => /[,"\r\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : v).join(',')
    ).join('\r\n')
    const blob = new Blob([csvText], { type: 'text/csv' })
    const a    = document.createElement('a')
    a.href     = URL.createObjectURL(blob)
    a.download = 'book-import-template.csv'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const newCount = rows.filter(r => {
    const t = String(r[mapping.title] || '').toLowerCase().trim()
    return t && !books.some(b => b.title.toLowerCase().trim() === t)
  }).length

  return (
    <Card label="Import books" title="Import from CSV">
      <p className="template-hint">
        Upload a <strong>.csv</strong> file. Columns can be in any order — the importer matches them automatically.{' '}
        <button className="template-link" onClick={downloadTemplate}>Download a ready-made template</button>
      </p>

      {rows.length === 0 ? (
        <div
          className={`import-drop ${dragging ? 'drag-over' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]) }}
        >
          <div className="import-drop-icon">📂</div>
          <p>Click to choose a file, or drag and drop here</p>
          <small>Supports .csv</small>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0])} />
        </div>
      ) : (
        <div className="import-preview">
          {/* Preview table */}
          <p style={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--stone)', fontWeight: 500, marginBottom: '0.75rem' }}>
            Preview — first 5 rows
          </p>
          <div className="import-table-wrap">
            <table className="import-table">
              <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.slice(0,5).map((r,i) => (
                  <tr key={i}>{headers.map(h => <td key={h} title={String(r[h])}>{String(r[h]).slice(0,30)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="import-summary">
            <span>Found <strong>{rows.length}</strong> row{rows.length !== 1 ? 's' : ''}</span>
            <span className="import-badge">{newCount} new</span>
            {rows.length - newCount > 0 && <span className="import-badge warn">{rows.length - newCount} already on shelf (skipped)</span>}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="save-btn ready" onClick={confirmImport}>Import books</button>
            <button className="ghost-btn" onClick={() => { setRows([]); setHeaders([]); setMapping({}) }}>Cancel</button>
            {done && <span style={{ fontSize: '0.82rem', color: 'var(--sage)', fontStyle: 'italic' }}>{done}</span>}
          </div>
        </div>
      )}
    </Card>
  )
}
